import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import './styles/index.css';
import AdminLayout from './layouts/AdminLayout';
import PublicLayout from './layouts/PublicLayout';
import Dashboard from './pages/Dashboard';
import Analytics from './pages/Analytics';
import PublicPlans from './pages/gardener/PublicPlans';
import DirectPlans from './pages/gardener/DirectPlans';
import PlanOffer from './pages/gardener/QuoteWizard';
import Projects from './pages/gardener/Projects';
import ProjectDetail from './pages/gardener/ProjectDetail';
import SentOfferView from './pages/gardener/SentOfferView';
import Tasks from './pages/Tasks';
import Plants from './pages/Plants';
import GardenQuestionnaire from './pages/seeker/GardenQuestionnaire';
import AIGeneratedPlanView from './pages/seeker/AIGeneratedPlanView';
import PlanDetails from './pages/seeker/PlanDetails';
import OffersForMyPlan from './pages/seeker/OffersForMyPlan';
import MyPlans from './pages/seeker/MyPlans';
import SeekerProjectDetail from './pages/seeker/SeekerProjectDetail';
import SeekerProjects from './pages/seeker/SeekerProjects';
import AdminModeration from './pages/admin/AdminModeration';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminEngagement from './pages/admin/AdminEngagement';
import AdminUserActivity from './pages/admin/AdminUserActivity';
import AdminIssues from './pages/admin/AdminIssues';
import AdminErrors from './pages/admin/AdminErrors';
import Calendar from './pages/Calendar';
import Schedule from './pages/gardener/Schedule';
import TeamPage from './pages/gardener/TeamPage';
import ConversationPage from './pages/ConversationPage';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import HomeLanding from './pages/public/HomeLanding';
import SeekerLanding from './pages/public/SeekerLanding';
import LoginPage from './pages/public/LoginPage';
import SavingsCalculator from './pages/public/SavingsCalculator';
import Gallery from './pages/public/Gallery';
import Resources from './pages/public/Resources';
import VerifyEmailPage from './pages/public/VerifyEmailPage';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import { AlertDialogProvider } from './shared/AlertDialog';
import RequireAuth from './shared/RequireAuth';
import RoleGate from './shared/RoleGate';
import { AuthProvider, useAuth } from './services/auth';
import { I18nProvider } from './i18n';
import { AdminViewAsProvider } from './lib/adminViewAs';

function AppLanding() {
	const { user } = useAuth();

	// RequireAuth should already ensure a user, but guard defensively
	if (!user) {
		return <Navigate to="/login" replace />;
	}

	if (user.role === 'seeker') {
		return <Navigate to="/app/seeker/plans" replace />;
	}

	return <Dashboard />;
}

const router = createBrowserRouter([
	{
		path: '/',
		element: <PublicLayout />,
		children: [
			{ index: true, element: <HomeLanding /> },
			{ path: 'login', element: <LoginPage /> },
			{ path: 'garden-seeker', element: <SeekerLanding /> },
			{ path: 'try-questionnaire', element: <GardenQuestionnaire guestMode /> },
			{ path: 'pricing', element: <Navigate to="/calculator" replace /> },
			{ path: 'calculator', element: <SavingsCalculator /> },
			{ path: 'gallery', element: <Gallery /> },
			{ path: 'resources', element: <Resources /> },
			{ path: 'privacy', element: <PrivacyPolicy /> },
			{ path: 'verify-email', element: <VerifyEmailPage /> }
		]
	},
	{
		path: '/app',
		element: <RequireAuth />,
		children: [
			{
				element: <AdminLayout />,
				children: [
					{ index: true, element: <AppLanding /> },
					{ path: 'home', element: <HomeLanding /> },
					{ path: 'dashboard', element: <Dashboard /> },
					{
						path: 'analytics',
						element: (
							<RoleGate allowed={['admin', 'company']}>
								<Analytics />
							</RoleGate>
						)
					},
					{
						path: 'team',
						element: (
							<RoleGate allowed={['admin', 'company']} appCapability="team">
								<TeamPage />
							</RoleGate>
						)
					},
					{ path: 'profile', element: <Profile /> },
					{ path: 'settings', element: <Settings /> },
					{
						path: 'calendar',
						element: <Calendar />
					},
					{
						path: 'conversation',
						element: <ConversationPage />
					},
					{
						path: 'schedule',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']} appCapability="schedule">
								<Schedule />
							</RoleGate>
						)
					},
					{
						path: 'company/my-plans',
						element: <Navigate to="/app/direct-plans" replace />
					},
					{
						path: 'direct-plans',
						element: (
							<RoleGate allowed={['admin', 'company']} appCapability="direct_plans">
								<DirectPlans />
							</RoleGate>
						)
					},
					{
						path: 'plans',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']} appCapability="marketplace">
								<PublicPlans />
							</RoleGate>
						)
					},
					{
						path: 'offers',
						element: <Navigate to="/app/projects?tab=offers" replace />
					},
					{
						path: 'plans/:planId',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']}>
								<PlanOffer />
							</RoleGate>
						)
					},
					{
						path: 'projects',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']} appCapability="projects">
								<Projects />
							</RoleGate>
						)
					},
					{
						path: 'sent-offer/:offerId',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']}>
								<SentOfferView />
							</RoleGate>
						)
					},
					{
						path: 'gardener/project/:projectId',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']}>
								<ProjectDetail />
							</RoleGate>
						)
					},
					{
						path: 'organization/schedule',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']}>
								<Navigate to="/app/schedule" replace />
							</RoleGate>
						)
					},
					{
						path: 'tasks',
						element: (
							<RoleGate allowed={['admin', 'company', 'gardener']} appCapability="tasks">
								<Tasks />
							</RoleGate>
						)
					},
					{
						path: 'plants',
						element: (
							<RoleGate allowed={['admin', 'gardener']}>
								<Plants />
							</RoleGate>
						)
					},
					{
						path: 'seeker/plans',
						element: (
							<RoleGate allowed={['admin', 'seeker']} appCapability="my_plans">
								<MyPlans />
							</RoleGate>
						)
					},
					{
						path: 'seeker/projects',
						element: (
							<RoleGate allowed={['admin', 'seeker']} appCapability="my_projects">
								<SeekerProjects />
							</RoleGate>
						)
					},
					{
						path: 'seeker/create-plan',
						element: (
							<RoleGate allowed={['admin', 'seeker', 'company']} appCapability="create_plan">
								<GardenQuestionnaire />
							</RoleGate>
						)
					},
					{
						path: 'seeker/generate-plan',
						element: (
							<RoleGate allowed={['admin', 'seeker', 'company']}>
								<AIGeneratedPlanView />
							</RoleGate>
						)
					},
					{
						path: 'seeker/plan/:planId',
						element: (
							<RoleGate allowed={['admin', 'seeker', 'company', 'gardener']}>
								<PlanDetails />
							</RoleGate>
						)
					},
					{
						path: 'seeker/plan/:planId/offers',
						element: (
							<RoleGate allowed={['admin', 'seeker']}>
								<OffersForMyPlan />
							</RoleGate>
						)
					},
					{
						path: 'seeker/project/:projectId',
						element: (
							<RoleGate allowed={['admin', 'seeker']}>
								<SeekerProjectDetail />
							</RoleGate>
						)
					},
					{
						path: 'admin/analytics',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_analytics">
								<AdminAnalytics />
							</RoleGate>
						)
					},
					{
						path: 'admin/user-activity',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_user_activity">
								<AdminUserActivity />
							</RoleGate>
						)
					},
					{
						path: 'admin/engagement',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_engagement">
								<AdminEngagement />
							</RoleGate>
						)
					},
					{
						path: 'admin/moderation',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_moderation">
								<AdminModeration />
							</RoleGate>
						)
					},
					{
						path: 'admin/issues',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_issues">
								<AdminIssues />
							</RoleGate>
						)
					},
					{
						path: 'admin/errors',
						element: (
							<RoleGate allowed={['admin']} adminCapability="admin_errors">
								<AdminErrors />
							</RoleGate>
						)
					}
				]
			}
		]
	},
	{
		path: '*',
		element: <Navigate to="/" replace />
	}
], {
	basename: (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '') // '' at hortera.org root
});

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<AuthProvider>
			<AdminViewAsProvider>
				<I18nProvider>
					<AlertDialogProvider>
						<RouterProvider router={router} />
					</AlertDialogProvider>
				</I18nProvider>
			</AdminViewAsProvider>
		</AuthProvider>
	</React.StrictMode>
);

